// apps/web/src/components/coletor/qr-scanner.tsx — Fase 02, Plano 07 (APT-02, D-09).
import { useState, useEffect, useRef, type ReactElement } from 'react';
import { BrowserQRCodeReader, type IScannerControls } from '@zxing/browser';

interface QrScannerProps {
  readonly onScan: (scannedText: string) => void;
  readonly onClose: () => void;
}

export function QrScanner({ onScan, onClose }: QrScannerProps): ReactElement {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [opening, setOpening] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const controlsRef = useRef<IScannerControls | null>(null);

  useEffect(() => {
    let active = true;
    const codeReader = new BrowserQRCodeReader();

    const startScanner = async () => {
      try {
        setOpening(true);
        setError(null);

        const videoInputDevices = await BrowserQRCodeReader.listVideoInputDevices();
        if (videoInputDevices.length === 0) {
          throw new Error('Nenhuma câmera encontrada no dispositivo.');
        }

        // Prefere câmera traseira (environment)
        const selectedDeviceId = videoInputDevices[videoInputDevices.length - 1]?.deviceId;

        if (!videoRef.current || !active) return;

        const controls = await codeReader.decodeFromVideoDevice(
          selectedDeviceId,
          videoRef.current,
          (result, err) => {
            if (result && active) {
              const text = result.getText();
              controls.stop();
              onScan(text);
            }
            if (err && !(err.name === 'NotFoundException')) {
              // NotFoundException ocorre a cada frame sem QR e deve ser ignorado
            }
          },
        );

        controlsRef.current = controls;
        if (active) {
          setOpening(false);
        }
      } catch (err) {
        if (active) {
          setOpening(false);
          setError(
            err instanceof Error && err.message.includes('câmera')
              ? err.message
              : 'Não foi possível ler o código. Tente novamente ou use o leitor USB.',
          );
        }
      }
    };

    void startScanner();

    return () => {
      active = false;
      controlsRef.current?.stop();
    };
  }, [onScan]);

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-slate-950/95 backdrop-blur-md p-4">
      <div className="relative w-full max-w-lg bg-slate-900 border border-slate-700 rounded-2xl overflow-hidden shadow-2xl p-4 flex flex-col items-center">
        <div className="w-full flex items-center justify-between pb-3 border-b border-slate-800">
          <h3 className="text-base font-bold text-white uppercase tracking-wider">Leitor de Câmera (QR Code)</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-white font-bold text-lg p-2"
          >
            ✕
          </button>
        </div>

        {/* Video Viewport */}
        <div className="relative w-full aspect-square max-w-sm my-4 bg-black rounded-xl overflow-hidden flex items-center justify-center border-2 border-dashed border-emerald-500/50">
          {opening && (
            <div className="absolute inset-0 flex items-center justify-center bg-slate-900/90 text-slate-300 text-sm font-medium z-10">
              <span className="animate-pulse">Abrindo câmera...</span>
            </div>
          )}

          {error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center bg-red-950/90 text-red-200 text-xs font-medium z-10 space-y-3">
              <span>{error}</span>
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-slate-800 text-white rounded text-xs font-bold"
              >
                Fechar
              </button>
            </div>
          )}

          <video ref={videoRef} className="w-full h-full object-cover" />

          {/* Mira / Scan Target Overlay */}
          {!opening && !error && (
            <div className="absolute inset-8 border-2 border-blue-400 rounded-lg pointer-events-none opacity-80 shadow-[0_0_15px_rgba(59,130,246,0.5)]" />
          )}
        </div>

        <p className="text-xs text-slate-400 text-center pb-2">
          Aponte a câmera para a etiqueta da OP, OS ou Lote. O leitor USB também permanece ativo.
        </p>

        <button
          type="button"
          onClick={onClose}
          className="w-full min-h-[52px] py-3 bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold text-sm rounded-xl transition"
        >
          Cancelar
        </button>
      </div>
    </div>
  );
}
